import React, { useEffect, useState } from 'react';
import { FiClock, FiGlobe } from 'react-icons/fi';
import { listRuns, type RunSummary } from '../../services/browserAgentService';
import {
  listSites, saveSite, deleteSite, addLogin, updateLogin, deleteLogin, setDefaultLogin,
  type AgentSite, type SiteLogin, type LoginExtraField,
} from './sitesStore';
import '../../styles/BrowserAgent.css';

type Tab = 'runs' | 'sites';

/** Right sidepanel: run History, Skills, and a merged Sites & Logins manager.
 *  Clicking a run opens it in the CENTER (via ba:view-run); clicking a skill
 *  runs it in the current session (via ba:run-skill). */
const BrowserAgentSidebarPanel: React.FC = () => {
  const [tab, setTab] = useState<Tab>('runs');
  const [runs, setRuns] = useState<RunSummary[]>([]);

  async function refreshRuns() {
    try { setRuns(await listRuns()); } catch { /* ignore */ }
  }

  useEffect(() => {
    refreshRuns();
    const h = () => refreshRuns();
    window.addEventListener('ba:refresh-history', h);
    const id = window.setInterval(refreshRuns, 10000);
    const toSites = () => setTab('sites');       // credential-less site click routes here
    window.addEventListener('ba:add-login', toSites);
    return () => {
      window.removeEventListener('ba:refresh-history', h);
      window.removeEventListener('ba:add-login', toSites);
      window.clearInterval(id);
    };
  }, []);

  return (
    <div className="ba-panel">
      <div className="ba-panel__topbar">
        <div className="ba-panel__tabs">
          <button className={tab === 'runs' ? 'is-active' : ''} onClick={() => setTab('runs')}>
            <FiClock size={12} /> Runs
          </button>
          <button className={tab === 'sites' ? 'is-active' : ''} onClick={() => setTab('sites')}>
            🌐 Sites &amp; Logins
          </button>
        </div>
        {tab === 'runs' && <button className="ba-panel__refresh" title="Refresh" onClick={refreshRuns}>⟳</button>}
      </div>

      <div className="ba-panel__view">
        {tab === 'runs' ? <RunsList runs={runs} /> : <SitesManager />}
      </div>
    </div>
  );
};

// ── Runs list (click → open in center) ─────────────────────────────────────────
const RunsList: React.FC<{ runs: RunSummary[] }> = ({ runs }) => (
  <div className="ba-panel__history">
    {runs.length === 0 && <div className="ba-panel__empty"><p>No runs yet.</p></div>}
    {runs.map((r) => (
      <button
        key={r.run_id}
        className="ba-runitem"
        onClick={() => window.dispatchEvent(new CustomEvent('ba:view-run', { detail: { runId: r.run_id } }))}
      >
        <span className={`ba-runitem__status ba-runitem__status--${r.status}`} />
        <div className="ba-runitem__info">
          <div className="ba-runitem__task">{r.task}</div>
          <div className="ba-runitem__meta">
            {r.starting_url && <span><FiGlobe size={10} /> {hostOf(r.starting_url)}</span>}
            <span>{r.step_count} steps</span>
            <span>{fmtTime(r.created_at)}</span>
          </div>
        </div>
      </button>
    ))}
  </div>
);

// ── Sites & Logins manager ─────────────────────────────────────────────────────
const SitesManager: React.FC = () => {
  const [sites, setSites] = useState<AgentSite[]>(() => listSites());
  const [addingSite, setAddingSite] = useState(false);
  const [editingSite, setEditingSite] = useState<string | null>(null);
  const [addingLoginFor, setAddingLoginFor] = useState<string | null>(null);
  const [editingLogin, setEditingLogin] = useState<{ siteId: string; loginId: string } | null>(null);
  const refresh = () => setSites(listSites());

  useEffect(() => {
    const h = () => refresh();
    window.addEventListener('ba:sites-changed', h);
    const addFor = (e: Event) => {
      const id = (e as CustomEvent).detail?.siteId;
      if (id) { setAddingLoginFor(id); setEditingSite(null); }
    };
    window.addEventListener('ba:add-login', addFor);
    return () => {
      window.removeEventListener('ba:sites-changed', h);
      window.removeEventListener('ba:add-login', addFor);
    };
  }, []);

  return (
    <div className="ba-logins">
      <button className="ba-input__btn ba-logins__add" onClick={() => setAddingSite((v) => !v)}>
        {addingSite ? 'Cancel' : '+ Add site'}
      </button>
      {addingSite && <SiteMetaForm onDone={() => { setAddingSite(false); refresh(); }} />}

      {sites.map((s) => (
        <div key={s.id} className="ba-site-card">
          {editingSite === s.id ? (
            <SiteMetaForm site={s} onDone={() => { setEditingSite(null); refresh(); }} />
          ) : (
            <div className="ba-site-card__head">
              <div className="ba-site-card__info">
                <div className="ba-login__label">{s.name}</div>
                <div className="ba-login__user">{s.loginUrl}</div>
              </div>
              <div className="ba-login__actions">
                <button title="Edit site" onClick={() => setEditingSite(s.id)}>✎</button>
                <button title="Delete site" onClick={() => { deleteSite(s.id); refresh(); }}>🗑</button>
              </div>
            </div>
          )}

          <div className="ba-site-card__logins">
            {s.logins.length === 0 && <div className="ba-site-card__empty">No logins yet.</div>}
            {s.logins.map((l) =>
              editingLogin && editingLogin.siteId === s.id && editingLogin.loginId === l.id ? (
                <LoginForm key={l.id} login={l} onSave={(f) => { updateLogin(s.id, l.id, f); setEditingLogin(null); refresh(); }} onCancel={() => setEditingLogin(null)} />
              ) : (
                <div key={l.id} className="ba-loginrow">
                  <button
                    className={`ba-loginrow__star ${s.defaultLoginId === l.id ? 'is-default' : ''}`}
                    title={s.defaultLoginId === l.id ? 'Default login' : 'Set as default'}
                    onClick={() => { setDefaultLogin(s.id, l.id); refresh(); }}
                  >
                    {s.defaultLoginId === l.id ? '★' : '☆'}
                  </button>
                  <div className="ba-loginrow__info">
                    <div className="ba-loginrow__label">{l.label}</div>
                    <div className="ba-loginrow__user">
                      {l.username} · ••••••
                      {l.extraFields && l.extraFields.length > 0 && (
                        <span className="ba-loginrow__extra"> · +{l.extraFields.length} field{l.extraFields.length > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  </div>
                  <div className="ba-login__actions">
                    <button title="Edit" onClick={() => setEditingLogin({ siteId: s.id, loginId: l.id })}>✎</button>
                    <button title="Delete" onClick={() => { deleteLogin(s.id, l.id); refresh(); }}>🗑</button>
                  </div>
                </div>
              )
            )}

            {addingLoginFor === s.id ? (
              <LoginForm onSave={(f) => { addLogin(s.id, f); setAddingLoginFor(null); refresh(); }} onCancel={() => setAddingLoginFor(null)} />
            ) : (
              <button className="ba-input__addlink" onClick={() => setAddingLoginFor(s.id)}>+ Add login</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const SiteMetaForm: React.FC<{ site?: AgentSite; onDone: () => void }> = ({ site, onDone }) => {
  const [name, setName] = useState(site?.name ?? '');
  const [loginUrl, setLoginUrl] = useState(site?.loginUrl ?? '');
  const valid = name.trim() && /^https?:\/\//.test(loginUrl.trim());
  return (
    <div className="ba-input__form ba-logins__form">
      <input className="ba-input__field" placeholder="Site name" value={name} onChange={(e) => setName(e.target.value)} />
      <input className="ba-input__field" placeholder="Login URL (https://…)" value={loginUrl} onChange={(e) => setLoginUrl(e.target.value)} />
      <div className="ba-input__actions">
        <button className="ba-input__btn ba-input__btn--ghost" onClick={onDone}>Cancel</button>
        <button className="ba-input__btn" disabled={!valid} onClick={() => { saveSite({ id: site?.id, name: name.trim(), loginUrl: loginUrl.trim() }); onDone(); }}>Save</button>
      </div>
    </div>
  );
};

const LoginForm: React.FC<{
  login?: SiteLogin;
  onSave: (f: { label: string; username: string; password: string; extraFields?: LoginExtraField[] }) => void;
  onCancel: () => void;
}> = ({ login, onSave, onCancel }) => {
  const [label, setLabel] = useState(login?.label ?? '');
  const [username, setUsername] = useState(login?.username ?? '');
  const [password, setPassword] = useState(login?.password ?? '');
  const [extras, setExtras] = useState<LoginExtraField[]>(login?.extraFields ?? []);

  const setExtra = (i: number, patch: Partial<LoginExtraField>) =>
    setExtras((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const addExtra = () => setExtras((prev) => [...prev, { label: '', value: '' }]);
  const removeExtra = (i: number) => setExtras((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div className="ba-input__form ba-logins__form">
      <input className="ba-input__field" placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
      <input className="ba-input__field" placeholder="Username / email" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
      <input className="ba-input__field" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />

      {/* Extra fields auto-filled on the login form (matched to inputs by their label). */}
      {extras.map((f, i) => (
        <div key={i} className="ba-extrafield">
          <input
            className="ba-input__field ba-extrafield__label"
            placeholder="Field label (e.g. Agency ID)"
            value={f.label}
            onChange={(e) => setExtra(i, { label: e.target.value })}
          />
          <input
            className="ba-input__field ba-extrafield__value"
            placeholder="Value"
            value={f.value}
            onChange={(e) => setExtra(i, { value: e.target.value })}
          />
          <button type="button" className="ba-extrafield__remove" title="Remove field" onClick={() => removeExtra(i)}>✕</button>
        </div>
      ))}
      <button type="button" className="ba-input__btn ba-input__btn--ghost ba-extrafield__add" onClick={addExtra}>+ Add field to auto-fill</button>

      <div className="ba-input__actions">
        <button className="ba-input__btn ba-input__btn--ghost" onClick={onCancel}>Cancel</button>
        <button className="ba-input__btn" disabled={!username || !password} onClick={() => onSave({ label, username, password, extraFields: extras })}>Save</button>
      </div>
    </div>
  );
};

function hostOf(url: string): string {
  try { return new URL(url).host; } catch { return url; }
}
function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default BrowserAgentSidebarPanel;
